import { useEffect, useState } from "react";
import { AlertTriangle, CheckCircle2, Copy, KeyRound, MessageSquareText, Wifi, WifiOff } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Checkbox } from "@/components/ui/checkbox";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { AGENT_SETTINGS } from "@/constants/testIds";
import { AGENT_MODEL_OPTIONS, OPENAI_MODEL_SUGGESTIONS } from "@/lib/agentConstants";
import { useToast } from "@/hooks/use-toast";
import api, { formatApiErrorDetail } from "@/lib/api";

/** Activacion, nombre/tono/prompt, idiomas habilitados, mensajes de
 * bienvenida/escalado, modelo LLM y estado de conexion de WhatsApp. */
export default function AgentConfigTab({ config, onSave }) {
  const [form, setForm] = useState(null);
  const [saving, setSaving] = useState(false);
  // Fix AJ (2026-10-23): prueba de conexion con OpenAI y confirmacion para quitar la clave.
  const [testing, setTesting] = useState(false);
  const [testResult, setTestResult] = useState(null);
  const [confirmRemoveKey, setConfirmRemoveKey] = useState(false);
  const { toast } = useToast();

  useEffect(() => {
    if (config) {
      setForm({
        is_active: config.is_active ?? true,
        assistant_name: config.assistant_name || "",
        tone: config.tone || "",
        system_prompt_extra: config.system_prompt_extra || "",
        enabled_languages: config.enabled_languages || ["es", "en"],
        welcome_message: config.welcome_message || "",
        escalation_message: config.escalation_message || "",
        model_value: `${config.llm_provider || "anthropic"}:${config.llm_model || "claude-sonnet-4-6"}`,
        llm_source: config.llm_source || "emergent",
        openai_model: config.openai_model || "gpt-4.1-mini",
        openai_key: "",
        advisor_name: config.advisor_name || "",
        advisor_whatsapp: config.advisor_whatsapp || "",
        auto_classify: config.auto_classify ?? true,
      });
      setConfirmRemoveKey(false);
    }
  }, [config]);

  if (!form) return <p className="text-sm text-muted-foreground">Cargando...</p>;

  const set = (field) => (e) => setForm((f) => ({ ...f, [field]: e.target.value }));

  const toggleLanguage = (lang) => {
    setForm((f) => {
      const current = f.enabled_languages;
      const next = current.includes(lang) ? current.filter((l) => l !== lang) : [...current, lang];
      return { ...f, enabled_languages: next.length ? next : current };
    });
  };

  const handleCopyWebhook = async () => {
    try {
      await navigator.clipboard.writeText(config.webhook_url || "");
      toast({ title: "URL del webhook copiada" });
    } catch (err) {
      toast({ title: "No se pudo copiar la URL", description: "Copiala manualmente desde el campo.", variant: "destructive" });
    }
  };

  const handleTestOpenai = async () => {
    setTesting(true);
    setTestResult(null);
    try {
      const { data } = await api.post("/agent/llm/test", { api_key: form.openai_key.trim() || null, model: form.openai_model.trim() || null }, { timeout: 60000 });
      setTestResult(data);
    } catch (err) {
      setTestResult({ ok: false, message: formatApiErrorDetail(err.response?.data?.detail) || "No se pudo probar la conexion.", models: [] });
    } finally {
      setTesting(false);
    }
  };

  const handleRemoveOpenaiKey = async () => {
    if (!confirmRemoveKey) { setConfirmRemoveKey(true); return; }
    setConfirmRemoveKey(false);
    setTestResult(null);
    await onSave({ openai_api_key: "" });
  };

  const handleSave = async () => {
    if (form.llm_source === "openai_own" && !config.openai_key_set && !form.openai_key.trim()) {
      toast({ title: "Falta la clave de OpenAI", description: "Pegá la clave de la cuenta de OpenAI para usarla.", variant: "destructive" });
      return;
    }
    setSaving(true);
    try {
      const [llm_provider, llm_model] = form.model_value.split(":");
      await onSave({
        llm_source: form.llm_source,
        openai_model: form.openai_model.trim() || null,
        ...(form.openai_key.trim() ? { openai_api_key: form.openai_key.trim() } : {}),
        // Fix Q (2026-10-01): "Agente activo" tambien se cambia desde Conversaciones.
        // Se manda solo si se toco aca, para no pisar ese cambio con un dato viejo.
        ...(form.is_active !== (config.is_active ?? true) ? { is_active: form.is_active } : {}),
        assistant_name: form.assistant_name || null,
        tone: form.tone,
        system_prompt_extra: form.system_prompt_extra || null,
        enabled_languages: form.enabled_languages,
        welcome_message: form.welcome_message || null,
        escalation_message: form.escalation_message || null,
        advisor_name: form.advisor_name.trim() || null,
        advisor_whatsapp: form.advisor_whatsapp.trim() || null,
        auto_classify: form.auto_classify,
        llm_provider,
        llm_model,
      });
    } finally {
      setSaving(false);
    }
  };

  const status = config.whatsapp_status || {};

  return (
    <div className="space-y-6 max-w-2xl">
      <div className="bg-white border border-border rounded-xl p-4 flex items-center justify-between gap-4" data-testid={AGENT_SETTINGS.whatsappStatusCard}>
        <div className="flex items-center gap-3">
          {status.connected ? <Wifi className="w-5 h-5 text-[#5B7A54]" /> : <WifiOff className="w-5 h-5 text-destructive" />}
          <div>
            <p className="text-sm font-medium text-foreground">{status.connected ? "WhatsApp conectado" : "WhatsApp desconectado"}</p>
            <p className="text-xs text-muted-foreground">{status.number ? `Número: ${status.number}${status.is_sandbox ? " (Sandbox de pruebas)" : ""}` : "No hay número configurado"}</p>
          </div>
        </div>
        <Badge className={status.connected ? "bg-[#E3EDE1] text-[#5B7A54] border-none" : "bg-destructive/10 text-destructive border-none"} data-testid={AGENT_SETTINGS.whatsappStatusBadge}>
          {status.connected ? "Conectado" : "Desconectado"}
        </Badge>
      </div>

      <div className="bg-white border border-border rounded-xl p-4 flex items-center justify-between gap-4">
        <div>
          <Label className="text-sm font-medium text-foreground">Agente activo</Label>
          <p className="text-xs text-muted-foreground mt-0.5">Cuando esta apagado, no se responde automaticamente a los mensajes (quedan en espera de un humano).</p>
        </div>
        <Switch checked={form.is_active} onCheckedChange={(v) => setForm((f) => ({ ...f, is_active: v }))} data-testid={AGENT_SETTINGS.activeToggle} />
      </div>

      {config.llm_last_error?.message && (
        <div className="bg-destructive/10 border border-destructive/30 rounded-xl p-4 flex items-start gap-3" data-testid={AGENT_SETTINGS.llmErrorBanner}>
          <AlertTriangle className="w-4 h-4 text-destructive mt-0.5 shrink-0" />
          <div>
            <p className="text-sm font-medium text-destructive">La IA no pudo responder el último mensaje</p>
            <p className="text-sm text-destructive/90 mt-0.5">{config.llm_last_error.message}</p>
            <p className="text-xs text-muted-foreground mt-1">Mientras tanto, los chats quedan en espera del equipo. Este aviso se borra solo al corregirlo y guardar.</p>
          </div>
        </div>
      )}

      <div className="bg-brand-light/40 border border-border rounded-xl p-4">
        <Label className="text-xs uppercase tracking-wide text-muted-foreground flex items-center gap-1.5 mb-2">
          <MessageSquareText className="w-3.5 h-3.5" /> URL del webhook (pegar en Twilio Console)
        </Label>
        <div className="flex items-center gap-2">
          <Input readOnly value={config.webhook_url || ""} data-testid={AGENT_SETTINGS.webhookUrlField} className="bg-white text-xs" />
          <Button variant="outline" size="icon" onClick={handleCopyWebhook} data-testid={AGENT_SETTINGS.copyWebhookButton}><Copy className="w-4 h-4" /></Button>
        </div>
        <p className="text-xs text-muted-foreground mt-2">
          Pegala en Twilio Console → Messaging → Try it Out → WhatsApp Sandbox → "When a message comes in" (metodo POST).
        </p>
      </div>

      <div>
        <Label className="text-sm text-muted-foreground mb-1 block">Nombre del asistente</Label>
        <Input value={form.assistant_name} onChange={set("assistant_name")} placeholder="Ej: Asistente Strani" data-testid={AGENT_SETTINGS.assistantNameInput} />
      </div>

      <div>
        <Label className="text-sm text-muted-foreground mb-1 block">Tono y personalidad</Label>
        <Textarea value={form.tone} onChange={set("tone")} rows={3} placeholder='Ej: "Calido, profesional y cercano. Usa un trato amable pero sin perder profesionalismo."' data-testid={AGENT_SETTINGS.toneInput} />
      </div>

      <div>
        <Label className="text-sm text-muted-foreground mb-1 block">Idiomas habilitados</Label>
        <div className="flex gap-4 mt-1">
          <label className="flex items-center gap-2 text-sm text-foreground cursor-pointer">
            <Checkbox checked={form.enabled_languages.includes("es")} onCheckedChange={() => toggleLanguage("es")} data-testid={AGENT_SETTINGS.languageEsCheckbox} /> Español
          </label>
          <label className="flex items-center gap-2 text-sm text-foreground cursor-pointer">
            <Checkbox checked={form.enabled_languages.includes("en")} onCheckedChange={() => toggleLanguage("en")} data-testid={AGENT_SETTINGS.languageEnCheckbox} /> English
          </label>
        </div>
      </div>

      <div>
        <Label className="text-sm text-muted-foreground mb-1 block">Mensaje de bienvenida (primer mensaje al lead)</Label>
        <Textarea value={form.welcome_message} onChange={set("welcome_message")} rows={2} placeholder="Ej: ¡Hola! Soy el asistente de Strani Just Because..." data-testid={AGENT_SETTINGS.welcomeMessageInput} />
      </div>

      <div>
        <Label className="text-sm text-muted-foreground mb-1 block">Mensaje al escalar a un humano</Label>
        <Textarea value={form.escalation_message} onChange={set("escalation_message")} rows={2} data-testid={AGENT_SETTINGS.escalationMessageInput} />
      </div>

      <div className="bg-white border border-border rounded-xl p-4 space-y-4">
        <div>
          <Label className="text-sm font-medium text-foreground mb-1 block">Derivación a una asesora</Label>
          <p className="text-xs text-muted-foreground">
            Cuando el agente pasa el chat al equipo (el lead lo pide o la conversación se enreda), el mensaje incluye un link para escribirle directo a la asesora por WhatsApp.
            El chat también queda en Conversaciones para que el equipo lo atienda desde acá. Si no cargás un número, no se agrega ningún link.
          </p>
        </div>
        <div className="grid gap-4 sm:grid-cols-2">
          <div>
            <Label className="text-sm text-muted-foreground mb-1 block">Nombre de la asesora (opcional)</Label>
            <Input value={form.advisor_name} onChange={set("advisor_name")} maxLength={60} placeholder="Ej: Camila" data-testid={AGENT_SETTINGS.advisorNameInput} />
          </div>
          <div>
            <Label className="text-sm text-muted-foreground mb-1 block">WhatsApp de la asesora</Label>
            <Input value={form.advisor_whatsapp} onChange={set("advisor_whatsapp")} inputMode="tel" placeholder="+1 713 493 5555" data-testid={AGENT_SETTINGS.advisorWhatsappInput} />
            <p className="text-xs text-muted-foreground mt-1">Con código de país.</p>
          </div>
        </div>
      </div>

      <div className="bg-white border border-border rounded-xl p-4 flex items-center justify-between gap-4">
        <div>
          <Label className="text-sm font-medium text-foreground">Etiquetar los chats automáticamente</Label>
          <p className="text-xs text-muted-foreground mt-0.5">El agente completa el servicio de interés y la etapa de cada chat. No cambia lo que el equipo ya puso.</p>
        </div>
        <Switch checked={form.auto_classify} onCheckedChange={(v) => setForm((f) => ({ ...f, auto_classify: v }))} data-testid={AGENT_SETTINGS.autoClassifyToggle} />
      </div>

      <div className="bg-white border border-border rounded-xl p-4 space-y-4">
        <div>
          <Label className="text-sm font-medium text-foreground mb-1 block">Motor de IA</Label>
          <Select value={form.llm_source} onValueChange={(v) => { setForm((f) => ({ ...f, llm_source: v })); setTestResult(null); }}>
            <SelectTrigger data-testid={AGENT_SETTINGS.llmSourceSelect}><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="emergent">Créditos de la plataforma</SelectItem>
              <SelectItem value="openai_own">Cuenta propia de OpenAI</SelectItem>
            </SelectContent>
          </Select>
          <p className="text-xs text-muted-foreground mt-1">
            {form.llm_source === "openai_own"
              ? "Las respuestas del agente se cobran en la cuenta de OpenAI de la clínica, no en los créditos de la plataforma."
              : "Las respuestas del agente usan los créditos de la plataforma."}
          </p>
        </div>

        {form.llm_source === "openai_own" && (
          <div className="space-y-4">
            <div>
              <Label className="text-sm text-muted-foreground mb-1 flex items-center gap-1.5"><KeyRound className="w-3.5 h-3.5" /> Clave de la API de OpenAI</Label>
              {config.openai_key_set && (
                <div className="flex items-center justify-between gap-3 mb-2 flex-wrap" data-testid={AGENT_SETTINGS.openaiKeySavedNote}>
                  <p className="text-sm text-foreground">Clave guardada (termina en <span className="font-mono">{config.openai_key_last4}</span>). Para cambiarla, pegá una nueva.</p>
                  <Button type="button" variant="outline" size="sm" onClick={handleRemoveOpenaiKey} data-testid={AGENT_SETTINGS.openaiKeyRemoveButton}>
                    {confirmRemoveKey ? "Confirmar: quitar la clave" : "Quitar clave"}
                  </Button>
                </div>
              )}
              <Input
                type="password" autoComplete="new-password" spellCheck={false} value={form.openai_key}
                onChange={(e) => { setForm((f) => ({ ...f, openai_key: e.target.value })); setTestResult(null); }}
                placeholder={config.openai_key_set ? "Pegá una clave nueva (opcional)" : "sk-..."} data-testid={AGENT_SETTINGS.openaiKeyInput}
              />
              <p className="text-xs text-muted-foreground mt-1">Se guarda cifrada y no se vuelve a mostrar. Se crea en platform.openai.com, en API keys.</p>
            </div>

            <div>
              <Label className="text-sm text-muted-foreground mb-1 block">Modelo de OpenAI</Label>
              <Input
                list="agent-openai-models" value={form.openai_model} onChange={(e) => { setForm((f) => ({ ...f, openai_model: e.target.value })); setTestResult(null); }}
                placeholder="gpt-4.1-mini" data-testid={AGENT_SETTINGS.openaiModelInput}
              />
              <datalist id="agent-openai-models">
                {(testResult?.models?.length ? testResult.models : OPENAI_MODEL_SUGGESTIONS).map((m) => <option key={m} value={m} />)}
              </datalist>
              <p className="text-xs text-muted-foreground mt-1">Al probar la conexión se listan los modelos que tu cuenta puede usar.</p>
            </div>

            <div className="flex items-center gap-3 flex-wrap">
              <Button type="button" variant="outline" onClick={handleTestOpenai} disabled={testing || (!config.openai_key_set && !form.openai_key.trim())} data-testid={AGENT_SETTINGS.openaiTestButton}>
                {testing ? "Probando..." : "Probar conexión"}
              </Button>
              {testResult && (
                <p className={`text-sm flex items-start gap-1.5 ${testResult.ok ? "text-[#5B7A54]" : "text-destructive"}`} data-testid={AGENT_SETTINGS.openaiTestResult}>
                  {testResult.ok ? <CheckCircle2 className="w-4 h-4 mt-0.5 shrink-0" /> : <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0" />}
                  <span>{testResult.message}</span>
                </p>
              )}
            </div>
          </div>
        )}
      </div>

      <div className={form.llm_source === "openai_own" ? "hidden" : ""}>
        <Label className="text-sm text-muted-foreground mb-1 block">Modelo LLM</Label>
        <Select value={form.model_value} onValueChange={(v) => setForm((f) => ({ ...f, model_value: v }))}>
          <SelectTrigger data-testid={AGENT_SETTINGS.modelSelect}><SelectValue /></SelectTrigger>
          <SelectContent>{AGENT_MODEL_OPTIONS.map((o) => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}</SelectContent>
        </Select>
      </div>

      <div>
        <Label className="text-sm text-muted-foreground mb-1 block">Instrucciones adicionales del prompt (modo avanzado, opcional)</Label>
        <Textarea value={form.system_prompt_extra} onChange={set("system_prompt_extra")} rows={3} placeholder="Ej: siempre firmar como el equipo de Strani Just Because." data-testid={AGENT_SETTINGS.systemPromptExtraInput} />
      </div>

      <Button onClick={handleSave} disabled={saving} className="bg-brand hover:bg-brand-hover text-white" data-testid={AGENT_SETTINGS.saveConfigButton}>
        {saving ? "Guardando..." : "Guardar configuracion"}
      </Button>
    </div>
  );
}
